import React, { useState, useEffect, useRef } from "react"
import { useFieldDragController } from "../controllers/field-drag-controller"
import Input from "aldoo-ra/Input"
import { XMarkIcon, Bars3Icon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import Tooltip from "aldoo-ra/Tooltip"
import LocalizationAIRewriteDialog from "aldoo-ra/Localization/localization-ai-rewrite-dialog"
import TypeResolver from "../type-resolver"
import DatabaseModelTypeResolver from "../database-model-type-resolver"
import _ from "lodash"
import Typography from "aldoo-ra/Typography"
import { showNotification } from "aldoo-ra/Notification"

const RobotIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h3a3 3 0 0 1 3 3v1h.5a1.5 1.5 0 0 1 0 3H19v1a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3v-1h-.5a1.5 1.5 0 0 1 0-3H5v-1a3 3 0 0 1 3-3h3V5.73A2 2 0 0 1 10 4a2 2 0 0 1 2-2zm-2 9a1.5 1.5 0 0 0 0 3 1.5 1.5 0 0 0 0-3zm4 0a1.5 1.5 0 0 0 0 3 1.5 1.5 0 0 0 0-3zm-5 5v1h6v-1H9z"/>
  </svg>
)

const formatName = (name) => {
  if (!name || typeof name !== "string") return String(name || "")
  return name
    .replace(/([A-Z][a-z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
}

export default function TextEditor({
  onChange,
  value,
  field,
  parentObject,
  parentValues,
  showInfo = true,
  readOnly = false,
  validationStatus,
  validationMessage,
  autoFocus = false,
}) {
  const inputRefs = useRef([])
  const isInternalChange = useRef(false)
  const firstInputRef = useRef(null)

  const { availableTypeNames } = TypeResolver()
  const { availableTypeNames: availableModelTypeNames } =
    DatabaseModelTypeResolver()

  const resolveOptions = (field) => {
    let { options } = field

    if (!options) return []

    if (Array.isArray(options) && options.every((option) => option?.resolver)) {
      const match = options.find((option) => {
        if (!option.type?.in) return false

        // Get parent type value - handle multiple formats
        const parentData = typeof parentObject === 'function' ? parentObject() : parentObject
        let parentType = parentData?.type

        // If it's an array, get first element
        if (Array.isArray(parentType)) {
          parentType = parentType[0]
        }

        // If it's an object with value property, extract value
        if (parentType && typeof parentType === 'object' && parentType.value) {
          parentType = parentType.value
        }

        // If it's still an object but no value property, might be {name, value}
        if (parentType && typeof parentType === 'object') {
          parentType = parentType.name || parentType.value
        }

        return option.type.in.includes(parentType)
      })

      if (!match) return []
      const { resolver } = match
      options = resolver
    }

    if (options === "{{TypeResolver.availableTypeNames}}") {
      return availableTypeNames().map((type) => ({
        value: type,
        name: formatName(type),
      }))
    }

    if (options === "{{DatabaseModelTypeResolver.availableTypeNames}}") {
      return availableModelTypeNames().map((type) => ({
        value: type,
        name: formatName(type),
      }))
    }

    if (options === "{{ContentType.fieldGroups}}") {
      const pValues = parentValues ? parentValues() : null
      const result = (
        pValues?.groups?.map((group) => ({
          value: group.id,
          name: formatName(group.label),
        })) || []
      )
      return result
    }

    if (Array.isArray(options)) {
      return options.map((option) => {
        if (typeof option === "string") {
          return {
            value: option,
            name: formatName(option),
          }
        }
        return option
      })
    }

    return options || []
  }

  const formatOptionValue = (val) => {
    if (val == null) return ""

    // For Input component (non-dropdown), we want just the value part
    if (!field.options || !field.options.length) {
      // If it's an object with value property, unwrap it to just the value
      // This handles corrupted data where objects were incorrectly stored
      if (val && typeof val === "object" && val.value !== undefined) {
        return val.value
      }
      return val
    }

    // From here on, we only handle fields WITH options

    if (Array.isArray(val) && val.length > 0 && typeof val[0] === "object") {
      return val[0]
    }

    // Get options and find matching option FIRST (even if value appears formatted)
    const options = resolveOptions(field)

    if (options && options.length > 0) {
      // Extract the value to match against (could be string or object.value)
      const valueToMatch = typeof val === "object" && val.value ? val.value : val
      const matchingOption = options.find((opt) => opt?.value === valueToMatch)
      if (matchingOption) return matchingOption
    }

    // If val is already in {value, name} format and no match found, return as is
    if (val && typeof val === "object" && "value" in val && "name" in val) return val

    // Enhanced fallback: if we have a custom renderer, create a more complete object
    if (field.onItemRender && typeof val === "string") {
      // Try to create a richer object for common product types
      const productTypeDescriptions = {
        purchase: "This is a normal product purchase",
        subscription: "Recurring payment product with auto-renewal",
        licence: "Product that grants access rights or permissions",
        bundle: "Package containing multiple products together"
      }

      return {
        value: val,
        name: val.charAt(0).toUpperCase() + val.slice(1),
        description: productTypeDescriptions[val] || `${val.charAt(0).toUpperCase() + val.slice(1)} product type`
      }
    }

    // Simple fallback for fields with options
    if (typeof val === "object" && val !== null) {
      return { value: val, name: formatName(val.label || val.name || val.type || JSON.stringify(val)) }
    }
    return { value: val, name: formatName(String(val)) }
  }

  const getInitialValue = () => {
    let defaultValue = field.default
    if (defaultValue !== undefined) {
      // Format single or multiple default values
      if (field.multiple) {
        defaultValue = Array.isArray(defaultValue)
          ? defaultValue.map(formatOptionValue)
          : [formatOptionValue(defaultValue)]
      } else {
        defaultValue = formatOptionValue(defaultValue)
      }
    }

    // Special handling for group field - extract value from object format if needed
    let processedValue = value
    if (field.id === 'group' && value && typeof value === 'object' && value.value) {
      processedValue = value.value
    }

    let initialValue =
      processedValue !== undefined && processedValue !== "" ? processedValue : defaultValue

    // Handle multiple values
    if (field.multiple) {
      if (!initialValue) {
        initialValue = []
      } else if (!Array.isArray(initialValue)) {
        initialValue = [formatOptionValue(initialValue)]
      } else {
        initialValue = initialValue.map(formatOptionValue)
      }
    } else if (initialValue !== undefined) {
      initialValue = formatOptionValue(initialValue)
    }


    return initialValue ?? (field.multiple ? [] : "")
  }

  const [localValue, setLocalValue] = useState(getInitialValue)
  const [duplicateFields, setDuplicateFields] = useState(new Set())
  const [multiSelectHasError, setMultiSelectHasError] = useState(false)
  const [aiRewriteIndex, setAiRewriteIndex] = useState(null)

  // Call onChange with initial value after mount
  useEffect(() => {
    const initialValue = getInitialValue()
    onChange?.(initialValue)
  }, []) // Empty dependency array ensures this only runs once after mount

  const parentData = typeof parentObject === 'function' ? parentObject() : parentObject
  const isRequired =
    typeof field.required === "function"
      ? field.required(parentData)
      : field.required

  //only set readOnly to true if field.readOnly is explicitly set to true
  if (field.readOnly === true) readOnly = true

  // Force component to recognize readOnly changes
  const readOnlyKey = `${field.id}-${readOnly ? 'readonly' : 'editable'}`

  // Unified function to update value and notify parent
  const updateValue = (newValue) => {
    isInternalChange.current = true
    setLocalValue(newValue)
    
    // Convert formatted objects back to simple values for the parent/storage
    let valueForParent = newValue
    if (field.options?.length) {
      if (field.multiple) {
        // For multiple values, extract the value property from each object
        valueForParent = Array.isArray(newValue) 
          ? newValue.map(item => item?.value || item)
          : newValue
      } else {
        // For single values, extract the value property
        valueForParent = newValue?.value || newValue
      }
    }
    
    
    onChange?.(valueForParent)
  }

  // Drag controller
  const { handleDragStart, handleDragOver, handleDragEnd } =
    useFieldDragController(localValue, updateValue)

  // Function to check if a value is duplicate
  const isDuplicateValue = (newValue, currentValues, excludeIndex = -1) => {
    if (!field.multiple || field.allowDuplicateSelection === true) return false

    const normalizeValue = (val) => {
      const actualValue = typeof val === "object" && val.value ? val.value : val
      return actualValue ? actualValue.toString().toLowerCase().trim() : ""
    }

    const normalizedNewValue = normalizeValue(newValue)
    if (!normalizedNewValue) return false

    const existingValues = (currentValues || [])
      .filter((_, index) => index !== excludeIndex)
      .map(normalizeValue)
      .filter(val => val !== "")

    return existingValues.includes(normalizedNewValue)
  }

  // Shared validation function for both blur and Enter scenarios
  const validateAndHandleDuplicate = (currentValue, index) => {
    // Skip validation for empty values or when duplicates are allowed
    if (!currentValue.trim() || !field.multiple || field.allowDuplicateSelection === true) {
      // Clear any error state for this field when validation is skipped
      setDuplicateFields(prev => {
        const newSet = new Set(prev)
        newSet.delete(index)
        return newSet
      })
      return false // No duplicate
    }

    // Check for duplicates
    if (isDuplicateValue(currentValue, localValue, index)) {
      showNotification({
        message: `"${currentValue}" already exists. ${field.label || "This field"} should contain unique values only. Please modify the value.`,
        className: "bg-admin_error text-white",
      })
      // Mark this field as having a duplicate
      setDuplicateFields(prev => new Set(prev).add(index))
      return true // Is duplicate
    } else {
      // Clear the error state for this field if it's no longer a duplicate
      setDuplicateFields(prev => {
        const newSet = new Set(prev)
        newSet.delete(index)
        return newSet
      })
      return false // No duplicate
    }
  }

  // Handle external value changes
  useEffect(() => {
    if (!isInternalChange.current) {
      const hasOptions = !!field.options && (Array.isArray(field.options) ? field.options.length > 0 : true)
      const currentValueStr = hasOptions
        ? JSON.stringify(
            localValue?.map?.((v) => v?.value) ?? localValue?.value
          )
        : JSON.stringify(localValue)
      const newValueStr = hasOptions
        ? JSON.stringify(value?.map?.((v) => v?.value) ?? value?.value)
        : JSON.stringify(value)

      if (currentValueStr !== newValueStr) {
        const newValue = getInitialValue()
        setLocalValue(newValue)
      }
    }
    isInternalChange.current = false
  }, [value, field.default, field.multiple])

  const handleValueChange = (updatedValue, index) => {
    try {
      // Clear error state when user starts typing to provide immediate feedback
      if (index !== undefined && duplicateFields.has(index)) {
        setDuplicateFields(prev => {
          const newSet = new Set(prev)
          newSet.delete(index)
          return newSet
        })
      }

      if (field.multiple) {
        const newValues = Array.isArray(localValue) ? [...localValue] : []

        if (index !== undefined) {
          newValues[index] = field.options?.length
            ? formatOptionValue(updatedValue)
            : updatedValue
        } else {
          newValues.push(
            field.options?.length
              ? formatOptionValue(updatedValue)
              : updatedValue
          )
        }

        updateValue(newValues)
      } else {
        updateValue(
          field.options?.length ? formatOptionValue(updatedValue) : updatedValue
        )
      }

      if (index !== undefined) {
        setTimeout(() => {
          inputRefs.current[index]?.focus()
        }, 0)
      }
    } catch (error) {
      console.error("Error in handleValueChange:", error)
      updateValue(field.multiple ? [] : "")
    }
  }

  const addNewInput = () => {
    handleValueChange("")
    setTimeout(() => {
      const lastInputIndex = localValue ? localValue.length : 0
      inputRefs.current[lastInputIndex]?.focus()
    }, 0)
  }

  const removeInput = (index) => {
    const updatedValues = localValue.filter((_, i) => i !== index)
    updateValue(updatedValues)
    
    // Clear duplicate state and adjust indices for remaining items
    setDuplicateFields(prev => {
      const newSet = new Set()
      prev.forEach(duplicateIndex => {
        if (duplicateIndex < index) {
          // Items before the removed index keep their index
          newSet.add(duplicateIndex)
        } else if (duplicateIndex > index) {
          // Items after the removed index shift down by 1
          newSet.add(duplicateIndex - 1)
        }
        // The removed item is excluded
      })
      return newSet
    })
  }

  const clearAll = () => {
    updateValue(field.multiple ? [] : "")
    setDuplicateFields(new Set())
  }

  const handleBlur = (e, index) => {
    const currentValue = e.target.value.trim()
    
    // Update the field with trimmed value if it changed
    if (currentValue !== e.target.value && field.multiple) {
      const newValues = Array.isArray(localValue) ? [...localValue] : []
      if (index !== undefined) {
        newValues[index] = field.options?.length
          ? formatOptionValue(currentValue)
          : currentValue
        updateValue(newValues)
      }
    }
    
    validateAndHandleDuplicate(currentValue, index)
  }

  const handleKeyDown = (e, index) => {
    if (!field.multiple || field.multiline) return
    if (e.key === "Enter") {
      e.preventDefault()
      
      // Trim the value before validation
      const currentValue = e.target.value.trim()
      
      // Update the field with trimmed value if it changed
      if (currentValue !== e.target.value) {
        const newValues = Array.isArray(localValue) ? [...localValue] : []
        if (index !== undefined) {
          newValues[index] = field.options?.length
            ? formatOptionValue(currentValue)
            : currentValue
          updateValue(newValues)
        }
      }
      
      const isDuplicate = validateAndHandleDuplicate(currentValue, index)
      
      // Only add new input if there's no duplicate
      if (!isDuplicate) {
        addNewInput()
      }
    }
  }

  const renderItem = (item, index) => (
    <div
      key={index}
      className="mt-2 gap-2 flex flex-row items-center w-full"
      draggable={!readOnly && field.multiple && localValue?.length > 1}
      onDragStart={!readOnly && field.multiple && localValue?.length > 1 ? () => handleDragStart(index) : undefined}
      onDragOver={!readOnly && field.multiple && localValue?.length > 1 ? (e) => {
        e.preventDefault()
        handleDragOver(index)
      } : undefined}
      onDragEnd={!readOnly && field.multiple && localValue?.length > 1 ? handleDragEnd : undefined}
    >
      {!readOnly && field.multiple && localValue?.length > 1 && (
        <Bars3Icon className="w-5 h-5 cursor-move text-admin_text_secondary mr-2" />
      )}

      {!!field.options?.length ? (
        <DropDownSelect
          variant={field.render?.variant || "admin"}
          menuPosition={field.render?.menuPosition || "bottom"}
          readOnly={readOnly}
          shouldResetSelection={field.multiple && localValue?.length > 0}
          zIndex={420}
          searchEnabled
          showItemNamesInSelection={true}
          multiSelection={field.multiple}
          showCheckbox={field.render?.showCheckbox !== false}
          showSelectionTick={field.render?.showSelectionTick || false}
          enableResetButton={field.render?.enableResetButton !== false}
          allowEmptySelection={field.render?.allowEmptySelection !== false}
          placeholder={field.optionsLabel}
          options={resolveOptions(field)}
          onItemRender={field.onItemRender || ((value) => value?.name)}
          onValueItemRender={field.onValueItemRender || field.onItemRender}
          onSelectionRender={field.onItemRender || ((value) => value?.name)}
          ref={(el) => (inputRefs.current[index] = el)}
          className={`flex-grow text-admin_text bg-admin_paper ${
            duplicateFields.has(index) 
              ? "!border-admin_error !border-2 focus:!ring-0 focus:!border-admin_error" 
              : ""
          }`}
          value={formatOptionValue(item)}
          onChange={(value) => handleValueChange(value, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
        />
      ) : (
        <Input
          variant={field.render?.variant || "admin"}
          ref={(el) => {
            inputRefs.current[index] = el
            if (index === 0 && autoFocus) {
              firstInputRef.current = el
            }
          }}
          multiline={field.multiline}
          readOnly={readOnly}
          labelClassName="text-admin_text"
          className={`flex-grow text-admin_text ${
            duplicateFields.has(index) ? "focus:ring-0 focus:border-admin_error" : ""
          }`}
          value={(item?.value ?? item) || ""}
          placeholder={field.placeholder || ""}
          onChange={(e) => handleValueChange(e.target.value, index)}
          onBlur={(e) => handleBlur(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          type={field.type === "Number" ? "number" : "text"}
          error={duplicateFields.has(index) ? " " : null} // Use space vs null to trigger error state
          autoFocus={index === 0 && autoFocus}
        />
      )}

      {!readOnly && !field.options?.length && (
        <Tooltip content="AI rewrite" position="top">
          <button
            onClick={() => setAiRewriteIndex(index)}
            className="w-8 h-8 rounded-full border border-admin_bordered flex items-center justify-center text-admin_primary hover:border-admin_primary transition-colors flex-shrink-0"
          >
            <RobotIcon className="w-4 h-4" />
          </button>
        </Tooltip>
      )}

      {!readOnly && field.multiple && localValue?.length > 1 && (
        <Button
          variant="admin_icon"
          className="text-admin_text hover:bg-admin_error hover:text-white rounded-full p-2 transition-colors"
          onClick={() => removeInput(index)}
          title="Remove this item"
        >
          <XMarkIcon className="w-6 h-6" />
        </Button>
      )}
    </div>
  )

  // Special handling for multi-select dropdown with options
  const renderMultiSelectDropdown = () => {
    // Convert stored string values back to {value, name} objects for display
    const options = resolveOptions(field)
    const displayValue = (localValue || []).map(storedValue => {
      // If storedValue is already an object, use it
      if (typeof storedValue === "object" && storedValue.value) return storedValue
      // If storedValue is a string, find the matching option
      const matchingOption = options.find(opt => opt.value === storedValue)
      return matchingOption || { value: storedValue, name: storedValue }
    })

    return (
      <div className="mt-2">
        <DropDownSelect
          variant={field.render?.variant || "admin"}
          menuPosition={field.render?.menuPosition || "bottom"}
          readOnly={readOnly}
          zIndex={420}
          searchEnabled={false}
          showItemNamesInSelection={true}
          showCheckbox={field.render?.showCheckbox !== false}
          showSelectionTick={field.render?.showSelectionTick !== false}
          enableResetButton={field.render?.enableResetButton !== false}
          allowEmptySelection={field.render?.allowEmptySelection !== false}
          multiSelection={true}
          fixedWidth={false}
          placeholder={field.optionsLabel || "Select options..."}
          options={options}
          onItemRender={field.onItemRender || ((value) => value?.name)}
          onValueItemRender={(() => {
            const renderer = field.onValueItemRender || field.onItemRender
            return renderer
          })()}
          onSelectionRender={field.onItemRender || ((value) => value?.name)}
          className={`w-full text-admin_text bg-admin_paper min-w-0 ${
            multiSelectHasError 
              ? "!border-admin_error !border-2 focus:!ring-0 focus:!border-admin_error" 
              : ""
          }`}
          value={displayValue}
          onChange={(selectedValues) => {
            // Clear error state when user starts selecting to provide immediate feedback
            if (multiSelectHasError) {
              setMultiSelectHasError(false)
            }

            // Convert {value, name} objects to simple string array for storage
            const stringValues = selectedValues.map(item => item.value)
            
            // Check for duplicates in multi-select dropdown when duplicates are not allowed
            if (field.allowDuplicateSelection !== true) {
              const normalizeValue = (val) => val ? val.toString().toLowerCase().trim() : ""
              const normalizedValues = stringValues.map(normalizeValue).filter(val => val !== "")
              const uniqueValues = new Set(normalizedValues)
              
              if (uniqueValues.size !== normalizedValues.length) {
                showNotification({
                  message: `Duplicate values detected. ${field.label || "This field"} should contain unique values only.`,
                  className: "bg-admin_error text-white",
                })
                setMultiSelectHasError(true)
                return
              }
            }
            
            updateValue(stringValues)
          }}
        />
      </div>
    )
  }

  return (
    <div className="text-editor">
      {aiRewriteIndex !== null && (
        <LocalizationAIRewriteDialog
          originalText={field.multiple ? (localValue?.[aiRewriteIndex] ?? "") : (localValue ?? "")}
          onAccept={(rewritten) => {
            if (field.multiple) {
              const updated = Array.isArray(localValue) ? [...localValue] : []
              updated[aiRewriteIndex] = rewritten
              updateValue(updated)
            } else {
              updateValue(rewritten)
            }
          }}
          onClose={() => setAiRewriteIndex(null)}
        />
      )}

      {/* Special case: multi-select dropdown with options */}
      {field.multiple && field.options?.length ? (
        renderMultiSelectDropdown()
      ) : (
        /* Normal case: individual inputs */
        Array.isArray(localValue)
          ? localValue?.map((value, index) => renderItem(value, index))
          : renderItem(localValue, 0)
      )}

      {/* Validation Status Display */}
      {validationStatus && (
        <div className="mt-2">
          {validationStatus === 'checking' && (
            <div className="flex items-center gap-2 text-sm text-admin_text_secondary">
              <div className="animate-spin w-4 h-4 border border-admin_text_secondary border-t-transparent rounded-full"></div>
              <span>Checking...</span>
            </div>
          )}
          {validationStatus === 'warning' && validationMessage && (
            <div className="flex items-center gap-2 text-sm text-admin_warning">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.962-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span>{validationMessage}</span>
            </div>
          )}
          {validationStatus === 'error' && validationMessage && (
            <div className="flex items-center gap-2 text-sm text-admin_error">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{validationMessage}</span>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-row justify-between items-center">
        <div className="flex items-center space-x-4 mt-4">
          {field.multiple &&
            Array.isArray(localValue) &&
            localValue?.length > 0 && (
              <span className="text-sm text-admin_text">
                {localValue.length} {localValue.length === 1 ? "item" : "items"}
              </span>
            )}

          {/* Only show Add/Clear buttons for multi-input mode, not multi-select dropdown */}
          {!readOnly && field.multiple && !field.options?.length && (
            <Button
              variant="admin_secondary"
              onClick={addNewInput}
            >
              Add
            </Button>
          )}

          {!readOnly &&
            field.multiple &&
            !field.options?.length &&
            Array.isArray(localValue) &&
            localValue?.length > 0 && (
              <Button
                variant="admin_secondary"
                onClick={clearAll}
              >
                Clear
              </Button>
            )}
        </div>
      </div>
    </div>
  )
}
